"use client";

import { useEffect, useMemo, useState } from "react";
import { useResolvedCity } from "@/hooks/useResolvedCity";
import { useTreatmentHref } from "@/hooks/useTreatmentHref";
import { useGetAllStoreCityAPIQuery } from "@/redux/rtkQueries/clientSideGetApis";
import { API_BASE_URL, FRONTEND_URL } from "@/util/config";
import { slugify } from "@/util/Slugify";

function isExternalHref(href: string) {
  return /^https?:\/\//i.test(href) || href.startsWith("//");
}

function stripQueryAndHash(path: string) {
  return path.split(/[?#]/)[0] || path;
}

/**
 * Parse an internal banner path into treatment slug (+ optional parent),
 * ignoring a leading city segment when it matches the store city list.
 */
function parseBannerTreatmentPath(
  href: string,
  cityNames: string[]
): {
  path: string;
  treatmentSlug: string | null;
  parentSlug?: string;
  alreadyHasCity: boolean;
} {
  const raw = href.trim();
  const withSlash = raw.startsWith("/") ? raw : `/${raw}`;
  const path = stripQueryAndHash(withSlash);
  const segments = path.split("/").filter(Boolean);
  const citySlugSet = new Set(cityNames.map((c) => slugify(c)).filter(Boolean));

  let rest = segments;
  let alreadyHasCity = false;
  if (segments.length > 0 && citySlugSet.has(slugify(segments[0]))) {
    alreadyHasCity = true;
    rest = segments.slice(1);
  }

  if (rest.length === 0) {
    return { path, treatmentSlug: null, alreadyHasCity };
  }
  if (rest.length === 1) {
    return { path, treatmentSlug: rest[0], alreadyHasCity };
  }
  return {
    path,
    parentSlug: rest[0],
    treatmentSlug: rest[1],
    alreadyHasCity,
  };
}

/**
 * Soft-check whether a slug is a treatment with a CMS template
 * (same source as `getTreatmeantTemplateLayoutTypeServerAPI`).
 * Uses fetch (not RTK) so 404s do not toast.
 */
async function fetchTreatmentTemplateExists(slug: string): Promise<boolean> {
  const base = (API_BASE_URL || "").replace(/\/$/, "");
  const url = `${base}${FRONTEND_URL}/treatmeant/template-type/${encodeURIComponent(slug)}`;
  try {
    const res = await fetch(url, { method: "GET", headers: { Accept: "application/json" } });
    if (!res.ok) return false;
    const json = (await res.json()) as { success?: boolean; data?: { name?: string } };
    return Boolean(json?.success && json?.data?.name);
  } catch {
    return false;
  }
}

/**
 * City-aware href for homepage banner `linkTo`, matching header TreatmentLink behavior:
 * - external URLs unchanged
 * - paths that already include a city segment unchanged
 * - if the path's treatment slug resolves via template-type API → prefix with resolved city
 * - otherwise keep the original path
 */
export function useCityAwareBannerHref(linkTo?: string | null): string {
  const resolvedCity = useResolvedCity();
  const { data: cityApi } = useGetAllStoreCityAPIQuery();
  const cityNames = useMemo(() => {
    const rows = cityApi?.data;
    if (!Array.isArray(rows)) return [] as string[];
    return rows.map((r) => r.cityName).filter(Boolean) as string[];
  }, [cityApi?.data]);

  const trimmed = typeof linkTo === "string" ? linkTo.trim() : "";
  const external = trimmed ? isExternalHref(trimmed) : false;

  const parsed = useMemo(() => {
    if (!trimmed || external) {
      return { path: trimmed, treatmentSlug: null as string | null, alreadyHasCity: false };
    }
    return parseBannerTreatmentPath(trimmed, cityNames);
  }, [trimmed, external, cityNames]);

  const [isTreatment, setIsTreatment] = useState(false);

  useEffect(() => {
    const slug = parsed.treatmentSlug;
    if (!slug || parsed.alreadyHasCity || external) {
      setIsTreatment(false);
      return;
    }
    let cancelled = false;
    fetchTreatmentTemplateExists(slug).then((ok) => {
      if (!cancelled) setIsTreatment(ok);
    });
    return () => {
      cancelled = true;
    };
  }, [parsed.treatmentSlug, parsed.alreadyHasCity, external]);

  const cityAwareHref = useTreatmentHref(
    parsed.treatmentSlug || "",
    parsed.parentSlug,
    resolvedCity || null
  );

  if (!trimmed) return "";
  if (external) return trimmed;
  if (parsed.alreadyHasCity) return parsed.path;
  if (isTreatment && parsed.treatmentSlug) return cityAwareHref;
  return parsed.path;
}

export { isExternalHref };
